import { TeamInLeague, League, Cup, CupFixture, Result, GameConfig } from '../domain/entities';

// Helper function to shuffle an array
const shuffleArray = <T>(array: T[]): T[] => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};

const createInitialCup = (cupName: string, leagues: Record<string, League>, gameType: 'mens' | 'womens', config: GameConfig): Cup => {
    const isWomens = gameType === 'womens';
    const cupConfig = (config.CUP_CONFIG as any)[cupName];

    let participatingTeams: string[];

    if (isWomens) {
        // Both women's cups include both leagues
        participatingTeams = config.LEAGUE_NAMES.flatMap(name => leagues[name]?.teams.map(t => t.name) || []);
    } else { // Men's cups
        if (cupName === 'Junior Cup') {
            // Junior Cup includes only League One and League Two
            const bottomTwoLeagueNames = [config.LEAGUE_NAMES[2], config.LEAGUE_NAMES[3]];
            participatingTeams = bottomTwoLeagueNames.flatMap(name => leagues[name]?.teams.map(t => t.name) || []);
        } else {
            // Pixel Cup includes all teams
            participatingTeams = Object.values(leagues).flatMap(l => l.teams).map(t => t.name);
        }
    }

    shuffleArray(participatingTeams);

    // Calculate number of rounds needed: ceil(log2(teamCount))
    // This ensures we have enough rounds to whittle down to 1 winner
    // With byes for odd team counts, we need: rounds = ceil(log2(teamCount))
    // Examples: 10 teams -> ceil(log2(10)) = ceil(3.32) = 4 rounds
    //           8 teams -> ceil(log2(8)) = 3 rounds
    //           16 teams -> ceil(log2(16)) = 4 rounds
    const teamCount = participatingTeams.length;
    const requiredRounds = Math.ceil(Math.log2(teamCount));

    // Generate round names dynamically based on required rounds
    const roundNames = generateRoundNames(requiredRounds);

    // Initialize rounds with placeholder week (0), will be updated by scheduler
    const rounds = roundNames.map((name: string, i: number) => ({
        name,
        week: 0,
        teams: i === 0 ? participatingTeams : [],
    }));

    return {
        name: cupName,
        currentRound: 0,
        prizeMoney: cupConfig.prizeMoney.slice(0, requiredRounds),
        winner: undefined,
        rounds,
    };
};

// Helper function to generate round names based on number of rounds needed
const generateRoundNames = (roundCount: number): string[] => {
    if (roundCount === 0) return [];
    if (roundCount === 1) return ['Final'];
    if (roundCount === 2) return ['Semi-Finals', 'Final'];
    if (roundCount === 3) return ['Quarter-Finals', 'Semi-Finals', 'Final'];

    // For 4+ rounds, use numbered rounds for early stages, then QF/SF/Final
    const names: string[] = [];
    const numericRounds = roundCount - 3; // Subtract 3 for QF, SF, Final

    for (let i = 1; i <= numericRounds; i++) {
        names.push(`Round ${i}`);
    }

    names.push('Quarter-Finals', 'Semi-Finals', 'Final');
    return names;
};

const generateRoundFixtures = (cup: Cup, existingFixtures: CupFixture[] = []): CupFixture[] => {
    const newFixtures: CupFixture[] = [];
    const roundInfo = cup.rounds[cup.currentRound];
    if (!roundInfo || roundInfo.teams.length === 0) return [];

    // Find teams that have already received a bye in previous rounds
    const teamsWithPreviousByes = new Set<string>();
    const previousRoundFixtures = existingFixtures.filter(f => {
        const roundIndex = cup.rounds.findIndex(r => r.name === f.roundName);
        return roundIndex >= 0 && roundIndex < cup.currentRound;
    });
    previousRoundFixtures.forEach(f => {
        if (f.away === 'BYE') {
            teamsWithPreviousByes.add(f.home);
        }
    });

    let teamsToDraw = [...roundInfo.teams];
    shuffleArray(teamsToDraw);

    // Calculate byes needed to ensure even number of teams for matches
    // Byes can occur in any round if the team count is odd
    // Formula: byesNeeded = totalTeams % 2 (0 or 1)
    // If odd number of teams, 1 team gets a bye to make matches possible
    // Prioritize giving byes to teams that haven't had one yet
    const byeTeams: string[] = [];
    const totalTeams = teamsToDraw.length;
    const byesNeeded = totalTeams % 2; // 1 if odd, 0 if even

    for (let i = 0; i < byesNeeded; i++) {
        // Try to find a team that hasn't had a bye before
        const teamWithoutPreviousBye = teamsToDraw.find(team => !teamsWithPreviousByes.has(team));
        if (teamWithoutPreviousBye) {
            byeTeams.push(teamWithoutPreviousBye);
            teamsToDraw = teamsToDraw.filter(team => team !== teamWithoutPreviousBye);
        } else {
            // If all remaining teams have had a bye, just pick one (shouldn't happen often)
            byeTeams.push(teamsToDraw.pop()!);
        }
    }

    // All cup matches for this round occur on the round's assigned week
    // Do not spread across multiple weeks - the scheduler has already allocated specific weeks
    while(teamsToDraw.length > 0) {
        const home = teamsToDraw.pop()!;
        const away = teamsToDraw.pop()!;

        newFixtures.push({
            roundName: roundInfo.name,
            home,
            away,
            week: roundInfo.week,
        });
    }

    // Each team with a bye advances automatically (no match result, just shows as BYE)
    // Byes are scheduled on the first week of the round
    byeTeams.forEach(byeTeam => {
         newFixtures.push({
            roundName: roundInfo.name,
            home: byeTeam,
            away: 'BYE',
            week: roundInfo.week,
            // Bye fixtures have no result object - they represent automatic advancement
        });
    });

    return newFixtures;
};

const advanceToNextRound = (cup: Cup, currentRoundFixtures: CupFixture[]): Cup => {
    const updatedCup: Cup = JSON.parse(JSON.stringify(cup));

    const winners = currentRoundFixtures.map((f: CupFixture) => {
        // Bye fixtures have no result - the home team automatically advances
        if (f.away === 'BYE' && !f.result) return f.home;
        // Regular fixtures need a result
        if (!f.result) return null;
        // Determine winner based on final score (which includes penalty shootout outcome)
        return f.result.homeScore > f.result.awayScore ? f.home : f.away;
    }).filter((w: string | null) => w !== null) as string[];

    updatedCup.currentRound++;

    if (updatedCup.currentRound < updatedCup.rounds.length) {
        updatedCup.rounds[updatedCup.currentRound].teams = winners;
    } else {
        updatedCup.winner = winners[0] || 'Unknown';
    }

    return updatedCup;
};


export const CupService = {
    createInitialCup,
    generateRoundFixtures,
    advanceToNextRound,
};
